import { GameState } from './GameState';
import { GameConfig } from '../core/GameConfig';
import { MetaData, SaveData, ShopPurchases } from '../data/types';
import { StorageAdapter } from '../platform';

/**
 * 存档系统
 * 使用 Platform 的 StorageAdapter（localStorage / 小程序桥）。
 */
export class SaveSystem {
    private static instance: SaveSystem;

    /**
     * 主存档两格（A/B 双键轮换，2026-10-13）：下标 0＝`SAVE_KEY_MAIN`＝历史单键，
     * 老档原地可读、零迁移；下标 1＝副键。每次写盘只写「较旧」那一格，
     * 于是任何时刻盘上都留着「最新一次完整写」＋「上一次的完整写」。
     */
    private mainSlots = [GameConfig.SAVE_KEY_MAIN, GameConfig.SAVE_KEY_MAIN_B];

    private constructor() { }

    static getInstance(): SaveSystem {
        if (!this.instance) {
            this.instance = new SaveSystem();
        }
        return this.instance;
    }

    /**
     * 保存游戏（A/B 轮换：写两格里较旧那一格）
     * 整盘 `JSON.stringify` 是同步写，移动端中途杀进程有极小概率留下半截 JSON；
     * 单键时那一格就此作废（读档抛异常、`hasSave` 变 false、「继续游戏」凭空消失＝整局蒸发），
     * 轮换后另一半还在，最多退回到上一个存档点。
     */
    save(): boolean {
        try {
            const state = GameState.getInstance();
            const data = state.toSave();
            const [a, b] = this.readBothSlots();
            // 写盘时间戳单调化（2026-10-15 治本）：两格的新旧判据只有 `Date.now()`，同一毫秒并列时
            // `pickSlot` 判给 A——于是「存完立刻读」会读回上一格那份旧档，整局往回退一格。
            // 实测复现＝每月连存带读的 soak：18 年 × 10 局倒带 37 次，每次两格 timestamp 完全相等
            // （`_test_premiere_soak.mjs` 那批「同一部剧同月开播播报两次」的红就是这么来的，不是产品侧重报）。
            // 现在写盘永远落在「盘上最新时间戳 + 1」之上，任何时刻两格都可辨。字段没变、只改取值，老档零迁移。
            data.timestamp = Math.max(data.timestamp, Math.max(this.tsOf(a), this.tsOf(b)) + 1);
            const json = JSON.stringify(data);
            const slot = this.staleSlotOf(a, b);
            StorageAdapter.setItem(slot, json);
            console.log(`[SaveSystem] 游戏已保存 (版本 ${data.version}，存档格 ${slot === this.mainSlots[0] ? 'A' : 'B'})`);
            return true;
        } catch (e) {
            console.error('[SaveSystem] 保存失败:', e);
            return false;
        }
    }

    /** 读取存档（两格都试，取能解析且 timestamp 较新的一格） */
    load(): SaveData | null {
        const data = this.pickSlot();
        if (data) console.log(`[SaveSystem] 存档已读取 (版本 ${data.version})`);
        return data;
    }

    /** 检查是否存在【当前版本可读】存档（旧版本视同无档，「继续游戏」按钮不会出现） */
    hasSave(): boolean {
        return this.pickSlot() !== null;
    }

    /** 删除存档（两格一起清，留半份等于删了个寂寞） */
    deleteSave(): void {
        this.mainSlots.forEach(slot => StorageAdapter.removeItem(slot));
        console.log('[SaveSystem] 存档已删除');
    }

    // ===== A/B 双键轮换 internals =====

    /**
     * 读一格：解析不了、或版本低于当前存档版本，一律当「这一格没有档」。
     * 版本不足的格算「旧」，所以下一次写盘会覆盖它——与轮换前的单键行为一致。
     */
    private readSlot(key: string): SaveData | null {
        try {
            const json = StorageAdapter.getItem(key);
            if (!json) return null;
            const data = JSON.parse(json) as SaveData;
            // 版本兼容检查（v6 起：属性尺度变更，旧档数值不可直读，低于当前版本拒载）
            if (typeof data.version !== 'number' || data.version < GameConfig.SAVE_VERSION) {
                console.warn(`[SaveSystem] 存档版本过低 (${data.version} < ${GameConfig.SAVE_VERSION})，无法读取`);
                return null;
            }
            return data;
        } catch (e) {
            console.warn(`[SaveSystem] 存档格 ${key} 读不出（半截写或坏键），改用另一格:`, e);
            return null;
        }
    }

    /** 挑「能解析且较新」的一格；两格都不成立才是无档 */
    private pickSlot(): SaveData | null {
        const [a, b] = this.readBothSlots();
        if (!a) return b;
        if (!b) return a;
        return this.isNewer(b, a) ? b : a;
    }

    /** 写盘落点＝较旧那一格（空格与坏格都算最旧，所以坏键会被下一次写就地修复）；两格的解析结果由调用方给，不许再解析一遍 */
    private staleSlotOf(a: SaveData | null, b: SaveData | null): string {
        if (!a) return this.mainSlots[0];
        if (!b) return this.mainSlots[1];
        return this.isNewer(b, a) ? this.mainSlots[0] : this.mainSlots[1];
    }

    private readBothSlots(): (SaveData | null)[] {
        return this.mainSlots.map(key => this.readSlot(key));
    }

    /** 时间戳读数：缺字段或坏值当 0（老档与半截写都归"最旧"） */
    private tsOf(data: SaveData | null): number {
        return data && typeof data.timestamp === 'number' ? data.timestamp : 0;
    }

    /**
     * 新旧判定只看 `timestamp`。自 2026-10-15 写盘单调化以后，**本进程新写的两格永不同毫秒并列**；
     * 并列只剩历史形态（老档两格恰好同毫秒），那时照旧判给 A——备用档仍在，不蒸发。
     */
    private isNewer(x: SaveData, y: SaveData): boolean {
        return this.tsOf(x) > this.tsOf(y);
    }

    // ===== 跨周目元数据 =====

    /** 读取跨周目元数据（含老版本字段迁移） */
    loadMeta(): MetaData {
        try {
            const json = StorageAdapter.getItem(GameConfig.SAVE_KEY_META);
            if (!json) {
                return this.defaultMeta();
            }
            const data = JSON.parse(json) as MetaData;
            const result: MetaData = {
                achievementPoints: data.achievementPoints ?? 0,
                unlockedAchievements: data.unlockedAchievements ?? [],
                shopPurchases: this.migrateShopPurchases(data.shopPurchases),
                shopUnlocked: data.shopUnlocked ?? false,
                randomPoolUnlocked: data.randomPoolUnlocked ?? false,
                // 结局批 2026-09-29：周目结算快照透传（缺省 undefined = 结算页回落现读内存）
                lastCareerSummary: data.lastCareerSummary,
            };
            return result;
        } catch (e) {
            console.error('[SaveSystem] 读取元数据失败:', e);
            return this.defaultMeta();
        }
    }

    /** 兼容老存档：shopPurchases 为 string[]（未用），迁移为结构化空购买状态 */
    private migrateShopPurchases(raw: unknown): ShopPurchases[] {
        if (!Array.isArray(raw)) return [];
        const first = raw[0];
        // 老结构是 string[]（概念页曾用 JSON 字符串，游戏内从未写入）→ 视为空
        if (typeof first === 'string') return [];
        // 2026-10-21 特性点数制：旧字段 `slots`（额外天赋槽购买数）**1:1 折成 `points`**（额外特性点），
        // 折算只在这一处做（读档顺手迁移，不升 SAVE_VERSION）——用户 2026-10-21 令「旧档照跑」。
        // 超上限的旧值（slots > 2）按上限夹住：商店购买跨周目累积，不封顶＝永久变强。
        return (raw as Array<Record<string, unknown>>).map(p => ({
            rank: (p.rank as number) ?? 0,
            attrs: (p.attrs ?? {}) as ShopPurchases['attrs'],
            points: Math.min((p.points as number) ?? (p.slots as number) ?? 0, GameConfig.TRAIT_POINTS_SHOP_MAX),
        }));
    }

    /** 保存跨周目元数据 */
    saveMeta(meta: MetaData): boolean {
        try {
            const json = JSON.stringify(meta);
            StorageAdapter.setItem(GameConfig.SAVE_KEY_META, json);
            return true;
        } catch (e) {
            console.error('[SaveSystem] 保存元数据失败:', e);
            return false;
        }
    }

    /** 更新元数据（合并增量） */
    updateMeta(updates: Partial<MetaData>): MetaData {
        const meta = this.loadMeta();
        if (updates.achievementPoints !== undefined) {
            meta.achievementPoints = updates.achievementPoints;
        }
        if (updates.unlockedAchievements !== undefined) {
            meta.unlockedAchievements = updates.unlockedAchievements;
        }
        if (updates.shopPurchases !== undefined) {
            meta.shopPurchases = updates.shopPurchases;
        }
        if (updates.shopUnlocked !== undefined) {
            meta.shopUnlocked = updates.shopUnlocked;
        }
        if (updates.randomPoolUnlocked !== undefined) {
            meta.randomPoolUnlocked = updates.randomPoolUnlocked;
        }
        this.saveMeta(meta);
        return meta;
    }

    /** 默认元数据 */
    private defaultMeta(): MetaData {
        return {
            achievementPoints: 0,
            unlockedAchievements: [],
            shopPurchases: [],
            shopUnlocked: false,
            randomPoolUnlocked: false,
        };
    }
}

/** 导出单例 */
export const saveSystem = SaveSystem.getInstance();
